import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Activity, Target, Users, Building2, Shield, TrendingUp, CheckCircle, Layers, BarChart3, AlertTriangle } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";
const culturePulseLogo = "/opt/photo/culturepulse-2.webp";

const whatItMeasures = [
  "Dominant cultural patterns and behavioural norms",
  "Alignment between stated values and lived experience",
  "Trust, psychological safety, and engagement signals",
  "Barriers to execution and change",
  "Ethical Climate and Behavioural Risk indicators"
];

const applicationAreas = [
  "Transformation and change initiatives",
  "Post-merger integration and cultural alignment",
  "Leadership effectiveness reviews",
  "Ethics, conduct, and behavioural risk assessments",
  "Employee engagement and retention analysis"
];

const insightLevels = [
  {
    title: "Organisation-wide patterns",
    description: "Aggregate view of cultural dynamics, strengths, and risks across the entire organisation"
  },
  {
    title: "Divisional and functional comparison",
    description: "How culture varies across business units, functions, and geographies"
  },
  {
    title: "Leadership culture signals",
    description: "How leadership behaviour shapes and reinforces organisational culture"
  },
  {
    title: "Change readiness indicators",
    description: "Organisational capacity to absorb and sustain transformation"
  }
];

const CulturePulsePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Organisational Culture Assessment Nigeria | Culture Survey Lagos"
        description="CulturePulse™ organisational culture assessment for Nigerian companies. Measure culture, behavioural dynamics, ethical climate, and values alignment. Trusted by Nigerian banks, multinationals, and government agencies for culture transformation."
        keywords="organisational culture assessment Nigeria, culture survey Lagos, employee culture survey Nigeria, workplace culture assessment Lagos, culture diagnostic Nigeria, organisational culture audit Africa, culture measurement Nigeria, ethics assessment Lagos"
        canonical="/culturepulse"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1573164574572-cb89e39749b4.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                CulturePulse™
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed mb-8">
                Organisational culture and behavioural dynamics assessment
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                A proprietary RESILIGENT diagnostic that reveals what your organisation's culture actually looks like, not what it claims to be.
              </p>
            </div>
            
            {/* Logo badge - top right */}
            <div className="absolute top-24 right-6 lg:right-8 xl:right-16 hidden md:block">
              <img 
                src={culturePulseLogo} 
                alt="CulturePulse" 
                className="h-16 lg:h-20 xl:h-24 w-auto opacity-90"
              />
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              <p className="text-slate-700 text-lg lg:text-xl leading-relaxed mb-6">
                CulturePulse™ is RESILIGENT's proprietary organisational diagnostic designed to surface the real culture of an organisation: the patterns of behaviour, decision-making, and interaction that shape how work actually gets done.
              </p>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Unlike engagement surveys or climate checks, CulturePulse™ focuses on behavioural dynamics, ethical climate, and the gap between espoused values and lived reality.
              </p>
              <div className="bg-[#2693d0]/5 rounded-xl p-6 border border-[#2693d0]/20">
                <p className="text-slate-800 font-medium text-lg leading-relaxed">
                  The result is an honest, data-informed view of organisational culture and health that supports transformation, risk management, and leadership development decisions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What It Measures */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide uppercase">Core Measurement</span>
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  What CulturePulse™ Measures
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Culture is not what organisations say it is. It is what people actually experience. CulturePulse™ captures this lived reality through targeted diagnostic questions and behavioural indicators.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 card-pulse-border">
                <p className="text-slate-700 font-medium text-lg mb-6">Key measurement areas:</p>
                <ul className="space-y-4">
                  {whatItMeasures.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Insight Levels */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Multi-Level Insight
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                CulturePulse™ provides insight at multiple levels of the organisation, enabling targeted intervention where it matters most.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {insightLevels.map((level, index) => (
                <div key={index} className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0] flex items-center justify-center text-white font-semibold text-sm">
                      {index + 1}
                    </div>
                    <h3 className="text-slate-800 font-semibold text-lg">{level.title}</h3>
                  </div>
                  <p className="text-slate-600 text-base leading-relaxed">{level.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Behavioural Risk */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide uppercase">Risk Insight</span>
                </div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Ethical Climate and Behavioural Risk
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                  CulturePulse™ includes specific indicators for ethical climate and behavioural risk, helping organisations identify vulnerabilities before they become incidents.
                </p>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                  This is particularly valuable for regulated industries, organisations undergoing change, and boards seeking assurance on conduct risk.
                </p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-8 border border-white/20">
                <p className="text-slate-200 font-medium text-lg mb-6">Risk indicators include:</p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Speak-up culture and psychological safety</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Pressure and performance culture dynamics</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Leadership behaviour and tone from the top</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Values-behaviour alignment signals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Applications */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Common Applications
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  CulturePulse™ is deployed wherever organisations need to understand their real culture, not just their intended one.
                </p>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                <ul className="space-y-4">
                  {applicationAreas.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Explore CulturePulse Button */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <a
                href="/product/culture-pulse"
                className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-5 py-3 rounded font-medium text-sm hover:bg-[#1e7ab8] transition-all duration-300"
              >
                Explore CulturePulse™
                <ArrowRight size={16} />
              </a>
              <p className="text-slate-500 text-sm mt-4">
                CulturePulse™ is part of the Impact AS Plus™ leadership and talent intelligence system.
              </p>
            </div>
          </div>
        </section>

        {/* Delivered via Impact AS Plus */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <img 
                  src={impactASPlusLogo} 
                  alt="Impact AS Plus" 
                  className="h-12 lg:h-14 w-auto mb-8"
                />
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  Delivered Through Impact AS Plus™
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  CulturePulse™ is administered and reported through Impact AS Plus™, enabling secure data collection at scale and integration with leadership and organisational development workflows.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 card-pulse-border">
                <p className="text-slate-700 font-medium text-lg mb-4">Platform integration enables:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Confidential, large-scale data collection</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Segmentation by division, function, level, and location</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Longitudinal tracking to measure change over time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Connection to leadership diagnostics and development</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Understand Your Organisation's Real Culture
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                Discover how CulturePulse™ can support transformation, risk management, and leadership development in your organisation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/briefing"
                  className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Book a leadership briefing
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-800 px-8 py-4 rounded-lg font-medium border border-slate-200 hover:border-[#2693d0] hover:text-[#2693d0] transition-all duration-300"
                >
                  Discuss your needs
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default CulturePulsePage;
